import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Seo } from "../components/Seo";
import { StepIndicator } from "../components/StepIndicator";
import { PaystubForm } from "../components/PaystubForm";
import { PaymentStep } from "../components/PaymentStep";
import { DeliveryStep } from "../components/DeliveryStep";
import { emptyForm } from "../lib/paystub";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Create = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState("form"); // form | payment | delivery
  const [form, setForm] = useState(emptyForm());
  const [paystubId, setPaystubId] = useState(null);
  const [paid, setPaid] = useState(false);
  const [revisionsRemaining, setRevisionsRemaining] = useState(1);

  const goto = (s) => {
    setStep(s);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const loadPaystub = async (id) => {
    const { data } = await axios.get(`${API}/paystubs/${id}`);
    setForm({ ...emptyForm(), ...data.form_data });
    setPaid(data.paid);
    setRevisionsRemaining(Math.max(0, 1 - (data.revisions_used || 0)));
  };

  const saveDraft = async () => {
    if (paystubId) {
      await axios.put(`${API}/paystubs/${paystubId}`, { form_data: form });
      return paystubId;
    }
    const { data } = await axios.post(`${API}/paystubs`, { form_data: form });
    setPaystubId(data.id);
    return data.id;
  };

  const validateForm = () => {
    if (!form.firstName || !form.lastName) return toast.error("Please enter the employee's first and last name."), false;
    if (!form.state) return toast.error("Please select a state of residence."), false;
    if (!form.companyName) return toast.error("Please enter the company name."), false;
    if (!form.payDate) return toast.error("Please select the Paycheck Date."), false;
    const ok = form.earnings.some((e) => parseFloat(e.payRate) > 0 && parseFloat(e.hours) > 0);
    if (!ok) return toast.error("Add at least one earnings line with pay rate and hours."), false;
    return true;
  };

  const handleFormNext = async () => {
    if (!validateForm()) return;
    try {
      if (paid) {
        const { data } = await axios.put(`${API}/paystubs/${paystubId}`, { form_data: form });
        setRevisionsRemaining(Math.max(0, 1 - (data.revisions_used || 0)));
        toast.success("Statement regenerated with your changes.");
        goto("delivery");
      } else {
        await saveDraft();
        goto("payment");
      }
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "No free revisions remaining." : "Something went wrong. Please try again.");
    }
  };

  const handlePaid = async () => {
    try { await loadPaystub(paystubId); } catch (e) { /* non-fatal */ }
    setPaid(true);
    setRevisionsRemaining(1);
    goto("delivery");
  };

  const startRevision = () => {
    if (revisionsRemaining <= 0) return toast.error("No free revisions remaining.");
    goto("form");
  };

  return (
    <>
      <Seo title="Create Your Paystub | Nexus Numbers" description="Create a professional paystub in minutes." robots="noindex, nofollow" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="grid lg:grid-cols-4 gap-10">
          <aside className="lg:col-span-1">
            <StepIndicator current={step} />
          </aside>
          <main className="lg:col-span-3">
            {step === "form" && (
              <PaystubForm form={form} setForm={setForm} onNext={handleFormNext}
                onBack={() => (paid ? goto("delivery") : navigate("/"))} />
            )}
            {step === "payment" && (
              <PaymentStep form={form} paystubId={paystubId} apiBase={API} onPaid={handlePaid} onBack={() => goto("form")} />
            )}
            {step === "delivery" && (
              <DeliveryStep form={form} paystubId={paystubId} apiBase={API}
                revisionsRemaining={revisionsRemaining} onRevise={startRevision} />
            )}
          </main>
        </div>
      </div>
    </>
  );
};
